import { useContext, useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Scale, X, ArrowLeft, Check, Minus, MapPin, Bed, Bath, Car, Maximize, Download, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import { useCompare } from '@/contexts/CompareContext';
import { AppContext } from '@/App';

const ComparePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { compareList, removeFromCompare, clearCompare } = useCompare();
  const { formatPrice, formatArea } = useContext(AppContext);
  const tableRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [selectedForPdf, setSelectedForPdf] = useState({});

  // Initialize all properties as selected when list changes
  useEffect(() => {
    const initial = {};
    compareList.forEach(p => { initial[p.id] = true; });
    setSelectedForPdf(initial);
  }, [compareList]);

  // Toggle selection for PDF
  const togglePdfSelection = (propertyId) => {
    setSelectedForPdf(prev => ({
      ...prev,
      [propertyId]: !prev[propertyId]
    }));
  };

  // Select/deselect all
  const toggleSelectAll = () => {
    const allSelected = compareList.every(p => selectedForPdf[p.id]);
    const newSelection = {};
    compareList.forEach(p => { newSelection[p.id] = !allSelected; });
    setSelectedForPdf(newSelection);
  };

  // Get selected properties for PDF
  const getSelectedProperties = () => {
    return compareList.filter(p => selectedForPdf[p.id]);
  };

  // Count selected
  const selectedCount = compareList.filter(p => selectedForPdf[p.id]).length;

  const downloadPDF = async () => {
    const selectedProps = getSelectedProperties();
    if (!tableRef.current || selectedProps.length < 1) return;
    
    setDownloading(true);
    try {
      await document.fonts?.ready;
      
      const canvas = await html2canvas(tableRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#0a0a0a',
        logging: false,
      });
      
      const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape for comparison
      const margin = 10;
      const pageWidth = pdf.internal.pageSize.getWidth() - margin * 2;
      const pageHeight = pdf.internal.pageSize.getHeight() - margin * 2;
      
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const image = canvas.toDataURL('image/png');
      
      // Add header
      pdf.setFillColor(5, 5, 5);
      pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight(), 'F');
      
      pdf.setTextColor(212, 175, 55);
      pdf.setFontSize(18);
      pdf.text('Ikonic Deals - Comparación de Propiedades', margin, 15);
      
      pdf.setTextColor(163, 163, 163);
      pdf.setFontSize(10);
      pdf.text(`Generado el ${new Date().toLocaleDateString('es-GT')}`, margin, 22);
      
      // Add comparison table image
      let y = 28;
      pdf.addImage(image, 'PNG', margin, y, imgWidth, imgHeight);
      
      // Handle multi-page if needed
      let remaining = imgHeight - (pageHeight - y);
      while (remaining > 0) {
        pdf.addPage();
        pdf.setFillColor(5, 5, 5);
        pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight(), 'F');
        y = margin - (imgHeight - remaining);
        pdf.addImage(image, 'PNG', margin, y, imgWidth, imgHeight);
        remaining -= pageHeight;
      }
      
      // Add footer with branding
      const lastPage = pdf.getNumberOfPages();
      pdf.setPage(lastPage);
      pdf.setTextColor(102, 102, 102);
      pdf.setFontSize(8);
      pdf.text('www.ikonicdeals.com | Tel: +502 4254-7204', margin, pdf.internal.pageSize.getHeight() - 5);
      
      pdf.save(`comparacion-propiedades-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setDownloading(false);
    }
  };

  const compareFields = [
    { key: 'price_usd', label: t('compare.price', 'Precio'), format: (v) => formatPrice(v) },
    { key: 'property_type', label: t('compare.type', 'Tipo'), format: (v) => v },
    { key: 'transaction_type', label: t('compare.transaction', 'Transacción'), format: (v) => v },
    { key: 'location', label: t('compare.location', 'Ubicación'), format: (v, p) => `${p.zone}, ${p.department}` },
    { key: 'area_m2', label: t('compare.area', 'Área'), format: (v) => formatArea(v) },
    { key: 'bedrooms', label: t('compare.bedrooms', 'Habitaciones'), format: (v) => v || '-', icon: Bed },
    { key: 'bathrooms', label: t('compare.bathrooms', 'Baños'), format: (v) => v || '-', icon: Bath },
    { key: 'parking_spaces', label: t('compare.parking', 'Estacionamientos'), format: (v) => v || '-', icon: Car },
  ];

  if (compareList.length < 2) {
    return (
      <>
        <SEO 
          title="Comparar Propiedades | Ikonic Deals"
          description="Compara propiedades lado a lado"
          noindex={true}
        />
        <Header />
        
        <section className="pt-32 pb-24 min-h-screen">
          <div className="container-custom px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <Scale size={64} className="mx-auto text-white/20 mb-6" />
              <h1 className="text-2xl text-white mb-4">
                {t('compare.needMore', 'Necesitas al menos 2 propiedades')}
              </h1>
              <p className="text-[#A3A3A3] mb-8 max-w-md mx-auto">
                {t('compare.addMore', 'Añade propiedades a la comparación haciendo clic en el botón de balanza en cada propiedad')}
              </p>
              <Link to="/propiedades">
                <Button className="bg-[#D4AF37] text-black hover:bg-[#FFE182]">
                  <ArrowLeft size={18} className="mr-2" />
                  {t('compare.goToProperties', 'Ver Propiedades')}
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO 
        title="Comparar Propiedades | Ikonic Deals"
        description="Compara propiedades lado a lado"
        noindex={true}
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-8 bg-gradient-to-b from-[#0a0a0a] to-[#050505]">
        <div className="container-custom px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <button 
                onClick={() => navigate(-1)}
                className="text-[#A3A3A3] hover:text-[#D4AF37] flex items-center gap-2 mb-4 transition-colors"
              >
                <ArrowLeft size={16} />
                {t('common.back')}
              </button>
              <h1 className="text-3xl md:text-4xl text-white flex items-center gap-3">
                <Scale className="text-[#D4AF37]" size={32} />
                {t('compare.title', 'Comparar Propiedades')}
              </h1>
              <p className="mt-2 text-[#A3A3A3]">
                {t('compare.comparing', 'Comparando {{count}} propiedades', { count: compareList.length })}
              </p>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={clearCompare}
                className="border-white/20 text-white hover:bg-white/10"
              >
                {t('compare.clearAll', 'Limpiar comparación')}
              </Button>
              
              <Button
                onClick={downloadPDF}
                disabled={downloading || selectedCount < 1}
                className="bg-[#D4AF37] text-black hover:bg-[#FFE182]"
                data-testid="download-comparison-pdf-btn"
              >
                {downloading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    {t('compare.generating', 'Generando...')}
                  </>
                ) : (
                  <>
                    <Download size={18} className="mr-2" />
                    {selectedCount < compareList.length 
                      ? t('compare.downloadSelected', 'Descargar {{count}} seleccionadas', { count: selectedCount })
                      : t('compare.downloadPDF', 'Descargar PDF')
                    }
                  </>
                )}
            </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Selection Controls */}
      <section className="pb-4">
        <div className="container-custom px-4 sm:px-6">
          <div className="flex items-center justify-between bg-[#121212] p-3 rounded-lg border border-white/5">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={compareList.length > 0 && compareList.every(p => selectedForPdf[p.id])}
                onChange={toggleSelectAll}
                className="w-5 h-5 rounded border-white/20 bg-transparent text-[#D4AF37] focus:ring-[#D4AF37] cursor-pointer"
                data-testid="select-all-checkbox"
              />
              <span className="text-white text-sm">
                {t('compare.selectAll', 'Seleccionar todas para PDF')}
              </span>
            </label>
            <span className="text-[#A3A3A3] text-sm">
              {selectedCount} de {compareList.length} seleccionadas
            </span>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-4 pb-24">
        <div className="container-custom px-4 sm:px-6">
          <div className="overflow-x-auto">
            <div ref={tableRef} className="bg-[#0a0a0a] p-4 rounded-lg">
            <table className="w-full min-w-[600px]" data-testid="comparison-table">
              {/* Property Headers */}
              <thead>
                <tr>
                  <th className="w-48 p-4"></th>
                  {compareList.map(property => (
                    <th key={property.id} className="p-4 text-left">
                      <div className="relative">
                        {/* Checkbox for PDF selection */}
                        <div className="absolute -top-2 -left-2 z-10">
                          <input
                            type="checkbox"
                            checked={!!selectedForPdf[property.id]}
                            onChange={() => togglePdfSelection(property.id)}
                            className="w-5 h-5 rounded border-white/20 bg-[#0a0a0a] text-[#D4AF37] focus:ring-[#D4AF37] cursor-pointer"
                            data-testid={`pdf-checkbox-${property.id}`}
                          />
                        </div>
                        <button
                          onClick={() => removeFromCompare(property.id)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors z-10"
                        >
                          <X size={14} />
                        </button>
                        <Link to={`/propiedad/${property.slug || property.id}`}>
                          <div className={`bg-[#121212] border overflow-hidden hover:border-[#D4AF37]/30 transition-colors ${
                            selectedForPdf[property.id] ? 'border-[#D4AF37]/50' : 'border-white/5'
                          }`}>
                            <div className="h-40 overflow-hidden">
                              <img 
                                src={property.images?.[0]?.url || 'https://via.placeholder.com/300x200'} 
                                alt={property.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="p-4">
                              <h3 className="text-white font-medium line-clamp-2 hover:text-[#D4AF37] transition-colors">
                                {property.name}
                              </h3>
                              <p className="text-[#A3A3A3] text-sm mt-1 flex items-center gap-1">
                                <MapPin size={12} />
                                {property.zone}
                              </p>
                            </div>
                          </div>
                        </Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              
              {/* Comparison Rows */}
              <tbody>
                {compareFields.map((field, index) => (
                  <tr 
                    key={field.key}
                    className={index % 2 === 0 ? 'bg-[#0a0a0a]' : 'bg-[#050505]'}
                  >
                    <td className="p-4 text-[#A3A3A3] font-medium flex items-center gap-2">
                      {field.icon && <field.icon size={16} className="text-[#D4AF37]" />}
                      {field.label}
                    </td>
                    {compareList.map(property => {
                      const value = field.format(property[field.key], property);
                      return (
                        <td key={property.id} className="p-4 text-white">
                          {field.key === 'price_usd' ? (
                            <span className="text-[#D4AF37] font-mono text-lg font-medium">
                              {value}
                            </span>
                          ) : (
                            value
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                
                {/* Features Row */}
                <tr className="bg-[#0a0a0a]">
                  <td className="p-4 text-[#A3A3A3] font-medium align-top">
                    {t('compare.features', 'Características')}
                  </td>
                  {compareList.map(property => (
                    <td key={property.id} className="p-4 text-white align-top">
                      <ul className="space-y-1">
                        {(property.features || []).slice(0, 5).map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm">
                            <Check size={14} className="text-[#D4AF37] flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                        {(!property.features || property.features.length === 0) && (
                          <li className="text-[#525252] text-sm">Sin características listadas</li>
                        )}
                      </ul>
                    </td>
                  ))}
                </tr>

                {/* CTA Row */}
                <tr>
                  <td className="p-4"></td>
                  {compareList.map(property => (
                    <td key={property.id} className="p-4">
                      <Link to={`/propiedad/${property.slug || property.id}`}>
                        <Button className="w-full bg-[#D4AF37] text-black hover:bg-[#FFE182]">
                          {t('compare.viewDetails', 'Ver Detalles')}
                        </Button>
                      </Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ComparePage;
